import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Slider from "react-slick";
import {
  Home as HomeIcon,
  Building,
  Palette,
  Ruler,
  Phone
} from 'lucide-react';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const services = [
  {
    icon: <HomeIcon className="w-6 h-6" />,
    title: 'Modular Kitchen',
    description: 'We offer complete architecture and design solutions with expert 2D & 3D interior designing.',
    images: ['/images/b-11.jpeg','/images/b-3.jpeg','/images/b-4.jpeg']
  },
  {
    icon: <Building className="w-6 h-6" />,
    title: '3D Elevation Design',
    description: 'Premium 3D elevation designs to visualize your dream project.',
    images: ['/images/3d.jpg','/images/2d.jpg']
  },
  {
    icon: <Palette className="w-6 h-6" />,
    title: 'Interior Design',
    description: 'Luxury interior designs blending comfort, style, and functionality.',
    images: ['/images/10.jpeg','/images/3.jpeg']
  },
  {
    icon: <Ruler className="w-6 h-6" />,
    title: 'Structural Design',
    description: 'Strong and safe structural planning with complete compliance.',
    images: ['/images/17.jpeg','/images/20.jpeg']
  }
];

const ServicesSection = () => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const sliderSettings = {
    dots: false,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 2500,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    pauseOnHover: false,
  };

  return (
    <section ref={ref} className="py-16 lg:py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            Our <span className="bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">Premium Services</span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
            High-end architectural & interior solutions crafted with excellence
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="overflow-hidden shadow-xl bg-[#111] border border-yellow-500/20 hover:border-yellow-500 transition-all flex flex-col"
            >
              
              {/* IMAGE SLIDER */}
              <div className="relative w-full h-48 sm:h-52 md:h-56 bg-black overflow-hidden">
                <Slider {...sliderSettings}>
                  {service.images.map((img, i) => (
                    <div key={i}>
                      <img
                        src={img}
                        alt={service.title}
                        className="w-full h-48 sm:h-52 md:h-56 object-cover"
                      />
                    </div>
                  ))}
                </Slider>

                {/* Icon */}
                <div className="absolute bottom-3 left-3 w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 text-black shadow-lg">
                  {service.icon}
                </div>
              </div>

              {/* Content */}
              <div className="p-5 text-center flex-1 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {service.description}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 mt-4 flex-wrap">
                  <a
                    href="https://wa.me/919119901924"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold py-2 rounded-lg hover:opacity-90 transition"
                  >
                    WhatsApp
                  </a>

                  <a
                    href="tel:+917311118002"
                    className="flex-1 flex items-center justify-center gap-2 border border-yellow-500 text-yellow-400 font-semibold py-2 rounded-lg hover:bg-yellow-500 hover:text-black transition"
                  >
                    <Phone className="w-5 h-5" />
                    Call
                  </a>
                </div>
              </div>

            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;